import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ADMIN_SIDEBAR_ANCHORS } from './spaces';

interface ModuleHeroProps {
  eyebrow?: string;
  title: string;
  description?: string;
  icon: LucideIcon;
  accent?: 'arc' | 'ice' | 'goal';
  /** Right-side header controls for page actions, filters, and selectors. */
  actions?: ReactNode;
  children?: ReactNode;
  status?: { label: string; tone?: 'ok' | 'warn' | 'info' | 'accent' };
}

/**
 * Reused at the top of every module page so navigation between spaces feels
 * intentional. Layout, weight, color all stay consistent — only content changes.
 */
export function ModuleHero({
  eyebrow,
  title,
  description,
  icon: Icon,
  accent = 'arc',
  actions,
  children,
  status,
}: ModuleHeroProps) {
  const actionSlot = actions ?? children;
  const location = useLocation();
  const activeAdminPage = ADMIN_SIDEBAR_ANCHORS.find((anchor) =>
    anchor.end
      ? location.pathname === anchor.path
      : location.pathname === anchor.path || location.pathname.startsWith(`${anchor.path}/`),
  );
  const repeatsTopbarTitle =
    Boolean(activeAdminPage) &&
    normalizeHeroLabel(title) === normalizeHeroLabel(activeAdminPage?.label ?? '');
  const purposeStatement = repeatsTopbarTitle ? description || activeAdminPage?.heroPurpose : undefined;

  return (
    <section className="hero">
      <div className="hero__lines" aria-hidden />
      <div className="hero__inner">
        <div className="hero__main">
          <div className="hero__icon" aria-hidden>
            <Icon size={26} />
          </div>
          <div className="hero__copy">
            {(eyebrow || status) && (
              <div className="hero__eyebrow-row">
                {eyebrow && <span className="eyebrow">{eyebrow}</span>}
                {status && (
                  <span className={`chip chip--${status.tone ?? 'accent'}`}>
                    <span className="chip__dot" />
                    {status.label}
                  </span>
                )}
              </div>
            )}
            {purposeStatement ? (
              <p className="hero__statement">{purposeStatement}</p>
            ) : (
              <>
                <h2 className="hero__title">{title}</h2>
                {description && <p className="hero__sub">{description}</p>}
              </>
            )}
            <div className={`accent-strip accent-strip--${accent}`} />
          </div>
        </div>
        {actionSlot && <div className="hero__actions">{actionSlot}</div>}
      </div>
    </section>
  );
}

function normalizeHeroLabel(value: string): string {
  return value.trim().toLowerCase().replace(/[.!?]+$/g, '');
}
